import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'app/profile/services/user.service';
import { Chart, ChartDataSets } from 'chart.js';
import * as moment from 'moment';
import { zip } from 'rxjs';
import {finalize} from 'rxjs/operators';

import { getTranslation } from 'shared/helpers/get-translation';

import { ThesesStats } from 'app/dialogs';

const LABEL_FORMAT: string = 'D.M.';

@Component({
  selector: 'bar-diagram',
  templateUrl: 'bar-diagram.html',
  styleUrls: ['bar-diagram.scss']})
export class BarDiagram {

  @ViewChild('chartCanvas', {static: true}) private _canvasRef: ElementRef;

  private _data: Array<ThesesStats>;
  private _groups: Array<string>;

  private _chart: Chart;
  private _datasetLabels: Array<string>;

  private _maxLabelCount: number = 3;

  @Input() public set data(data: Array<ThesesStats>) {
    this._data = data;
    this.initChart();
  };

  @Input() public set groups(groups: Array<string>) {
    this._groups = groups;
    this.initChart();
  };

  @Input() public scaleType = 'thumbs';

  private static createEmptyData(): Array<number> {
    return [null, null, null, null, null, null, null];
  }

  private static createLabels(start: Date): Array<string> {
    const date: moment.Moment = moment(start);

    const labels: Array<string> = [date.format(LABEL_FORMAT)];

    const entryCount: number = 6;

    for (let i: number = 0; i < entryCount; i++) {
      date.add(1, 'day');
      labels.push(date.format(LABEL_FORMAT));
    }

    return labels;
  }

  public get isLoading(): boolean {
    return this._data === undefined || this._groups === undefined;
  }

  private initChart(): void {

    if ( this.isLoading ) {
      return;
    }
    const canvas: HTMLCanvasElement = this._canvasRef.nativeElement;
    const isDeka = false; // (this.userService.getTheme() === 'deka' || this.userService.getTheme() === 'fcb');
    let fontColor = 'white';
    let strokeColor1 = 'rgb(255, 0, 0)';
    let strokeColor2 = 'rgb(128, 0, 0)';
    let strokeColor3 = 'rgb(128, 128, 0)';
    let strokeLaneColor = 'rgb(128, 128, 128)';
    try {
      fontColor = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--app-text-color');
      strokeColor1 = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--stroke-color1');
      strokeColor2 = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--stroke-color2');
      strokeColor3 = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--stroke-color3');
      strokeLaneColor = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--stroke-lane-color');
    } catch (e) {
      // ignore
    }
    const labels = this.makeLabels();
    const datasets = [];
    for (let i = 0; i < this._groups.length; i++) {
      const dSet = this.makeData(this._groups[i]);
      if (dSet !== undefined) {
        datasets.push({
          type: 'line',
          label: this._groups[i],
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderColor: (i == 0 ? strokeColor2 : strokeColor1),
          pointBackgroundColor: strokeColor3,
          pointBorderColor: strokeColor3,
          pointBorderWidth: 0,
          pointRadius: i == 0 ? 7 : 0,
          borderWidth: 14,
          borderCapStyle: 'round',
          data: dSet,
        },);
      }
    }
    this._chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: datasets,
      },
      options: {
        layout: {
          padding: 10,
        },
        legend: {
          display: false,
          position: 'bottom',
          labels: {
            fontColor: fontColor,
            boxWidth: 16,
          },
          onClick: (event, item) => { event.stopImmediatePropagation(); },
        },
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          yAxes: [
            {
              id: 'y-axis-0',
              position: 'left',
              display: true,
              ticks: {
                min: 0.5,
                max: 5.5,
                fontColor: 'rgba(0, 0, 0, 0)',
              },
              gridLines: {
                display: false,
                color: strokeLaneColor,
                zeroLineColor: strokeLaneColor,
                drawBorder: false,
              },
/*              scaleLabel: {
                display: false,
              }*/
            },
            // tslint:disable-next-line:no-any
          ] as Array<any>,
          xAxes: [{
            ticks: {
              fontColor: fontColor,
            },
            gridLines: {
              display: true,
              drawTicks: false,
            },
            // tslint:disable-next-line:no-any
          }] as Array<any>,
        },
      },
    });
  }
  private makeLabels(): Array<string> {
    const res = [];
    let l = 1;
    if ( this._data !== undefined && this._data.length > 0 ) {
      const r = this._data[0].range;
      if ( r == 'w' ) {
        l = 7;
      }
      else if ( r == 'm' ) {
        l = 4;
      }
      else if ( r == 'q' ) {
        l = 3;
      }

    }
    for(let i = 0; i < l; i++) {
      res.push('');
    }
    return res;
  }

  private makeData(group: string): any {
    const redData = this._data.filter(a => a.group == group);
    if ( redData.length == 0) {
      return undefined;
    }
    const res = [];
    redData.forEach(d => {
      res[d.index] = d.val;
    })
    return res;
  }
}
